<template>
    <div id="follow-up-timeline-widget-component">
        <WidgetWrapperComponent :title="$t(this.$props.title)" id-widget="follow-up-timeline-widget">
            <template v-slot:default>
                <template v-if="userHasAccess(asset_module.designation, role_vision)">
                    <div class="mb-4 d-flex flex-wrap justify-space-between align-center px-0">
                        <AddFollowUpDialogComponent
                            ref="addFollowUpDialog"
                            :asset="asset"
                            :type-relatable="NoteTypeRelatable.ASSETS"
                            @added:follow-up="fetchFollowUpByTypeRelatableAndIdRelatableDatatable"
                            @updated:follow-up="fetchFollowUpByTypeRelatableAndIdRelatableDatatable"
                        />

                        <v-switch
                            v-model="display_traceability"
                            :label="$t('global.show_traceability')"
                            class="mb-3 mx-4"
                            color="primary"
                            density="comfortable"
                            hide-details
                            inset
                            @change="fetchFollowUpByTypeRelatableAndIdRelatableDatatable"
                        />

                        <div :class="`${is_mobile ? 'w-100' : 'w-50'}`">
                            <v-text-field
                                v-model.trim="datatable.search"
                                :label="$t('global.search')"
                                dense
                                filled
                                hide-details
                                prepend-inner-icon="mdi-magnify"
                            >
                                <template v-slot:append-inner>
                                    <v-icon v-if="is_mobile" class="cursor-pointer" color="primary"> mdi-tune-variant </v-icon>
                                </template>
                            </v-text-field>
                        </div>
                    </div>

                    <template v-if="followUps.length > 0 && !datatable.loading">
                        <v-timeline align-top dense>
                            <v-timeline-item
                                v-for="followUp in followUps"
                                :key="followUp.id_follow_up"
                                :color="followUp.user?.badge_color ?? '#ccc'"
                                fill-dot
                                right
                                width="100%"
                            >
                                <template v-slot:icon>
                                    <v-tooltip v-if="followUp.user" location="top" open-delay="200">
                                        <template v-slot:default>
                                            {{ `${followUp.user?.first_name} ${followUp.user?.last_name} ` }}
                                        </template>
                                        <template v-slot:activator="{ props }">
                                            <span class="cursor-pointer font-semibold white--text" v-bind="props">
                                                {{ followUp.user?.initial }}
                                            </span>
                                        </template>
                                    </v-tooltip>
                                </template>

                                <template v-slot:default>
                                    <div class="d-flex text-base text-skin-default justify-space-between pt-2">
                                        <div>
                                            <template v-if="followUp.user">
                                                {{ `${followUp.user?.first_name} ${followUp.user?.last_name} ` }}
                                            </template>
                                        </div>

                                        <p class="mb-0">
                                            {{
                                                moment(followUp.date_creation).format(
                                                    getFormatDateBaseOnLanguage().dateFormatCompleteReadable
                                                )
                                            }}
                                        </p>
                                    </div>

                                    <div class="p-4 m-5 rounded pa-4 background-color-main lighten-5">
                                        <template v-if="followUp.type === 'note'">
                                            <template v-if="followUp.tag_referentiel">
                                                <div class="d-flex justify-space-between align-content-center">
                                                    <div class="d-flex align-center gap-1">
                                                        <p class="mb-0 subtitle-2">
                                                            {{ $t('follow_up_timeline_widget_component.situation') }} :
                                                        </p>
                                                        <TagChipComponent :tag-referentiel="followUp.tag_referentiel" size="small" />
                                                    </div>
                                                    <MenuActionsComponent
                                                        :actions="[
                                                            {
                                                                title: $t('global.modify'),
                                                                icon: 'mdi-application-edit-outline',
                                                                emit: 'openPutUpdateFollowUpDialog',
                                                                has_access:
                                                                    hasRightToEdit &&
                                                                    followUp.type === 'note' &&
                                                                    $auth.user().id_user === followUp.user?.id_user,
                                                            },
                                                        ]"
                                                        v-on:openPutUpdateFollowUpDialog="openPutUpdateFollowUpDialog(followUp)"
                                                    >
                                                        <template v-slot:icon>
                                                            <v-icon> mdi-dots-vertical</v-icon>
                                                        </template>
                                                    </MenuActionsComponent>
                                                </div>
                                                <hr class="my-2" />
                                            </template>
                                            <div>
                                                <div v-if="followUp.description" class="mb-2">
                                                    <p class="mb-0 subtitle-2">
                                                        {{ $t('follow_up_timeline_widget_component.description') }} :
                                                    </p>
                                                    <pre>{{ $t(followUp.description) }}</pre>
                                                </div>
                                                <div v-if="followUp.additional_data" class="mb-2">
                                                    <p class="mb-0 subtitle-2">{{ $t('global.date') }} :</p>
                                                    <span>
                                                        {{
                                                            moment(parseDate(followUp.additional_data)).format(
                                                                getFormatDateBaseOnLanguage().dateFormat
                                                            )
                                                        }}
                                                    </span>
                                                </div>
                                            </div>
                                        </template>

                                        <template v-else>
                                            {{ $t('follow_up_timeline_widget_component.' + followUp.description) }}
                                        </template>
                                    </div>
                                </template>
                            </v-timeline-item>
                        </v-timeline>

                        <div class="elements-per-page-container mt-2 py-2">
                            <span class="subtitle-2">{{ $t('global_datatable.row_per_page') }} : </span>
                            <v-select
                                v-model="datatable.itemsPerPage"
                                :item-title="(item) => item"
                                :items="[5, 10, 25, 50]"
                                class="select-elements-per-page mx-1"
                                dense
                                filled
                                hide-details
                                v-on:update:model-value="fetchFollowUpByTypeRelatableAndIdRelatableDatatable"
                            />
                            <v-pagination
                                v-model="datatable.currentPage"
                                :length="Math.ceil(datatable.total / datatable.itemsPerPage)"
                                total-visible="3"
                                v-on:update:model-value="fetchFollowUpByTypeRelatableAndIdRelatableDatatable"
                            />
                        </div>
                    </template>
                    <template v-else>
                        {{ $t('global.no_access_widget') }}
                    </template>
                </template>
            </template>
        </WidgetWrapperComponent>
    </div>
</template>

<script>
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import followUpService from '@/repositories/followUpRepository'
import AddEditFollowUpDialogComponent from '@/components/equipment/generic/form/AddEditFollowUpDialogComponent.vue'
import TagChipComponent from '@/components/settings/tag/TagChipComponent.vue'
import { NoteTypeRelatable } from '@/models/INote'
import MenuActionsComponent from '@/components/global/containment/MenuActionsComponent.vue'
import auth from '@/plugins/auth'

export default {
    name: 'FollowUpTimelineWidgetComponent',
    props: {
        idRelatable: {
            type: Number,
            required: true,
        },
        typeRelatable: {
            type: String,
            required: true,
        },
        hasRightToEdit: {
            type: Boolean,
            required: false,
            default: false,
        },
        title: {
            type: String,
            required: true,
        },
        asset: {
            type: Object,
            required: true,
        },
        asset_module: {
            type: Object,
            required: true,
        },
    },
    components: {
        MenuActionsComponent,
        TagChipComponent,
        AddFollowUpDialogComponent: AddEditFollowUpDialogComponent,
        WidgetWrapperComponent,
    },
    data() {
        return {
            display_traceability: false,
            fetching_follow_up_by_entity_relatable: true,
            followUps: [],
            note: {},
            datatable: {
                currentPage: 1,
                search: '',
                itemsPerPage: 5,
                total: 0,
                loading: false,
                buildUrlParams() {
                    let params = {
                        search: this.search,
                        limit: this.itemsPerPage,
                        offset: this.itemsPerPage * (this.currentPage - 1),
                    }

                    let result = ''
                    Object.keys(params).forEach((key) => {
                        result += `${key}=${params[key]}&`
                    })

                    return result
                },
            },
            loading: false,
            dialog_add: false,
        }
    },
    computed: {
        auth() {
            return auth
        },
        NoteTypeRelatable() {
            return NoteTypeRelatable
        },
    },
    methods: {
        fetchFollowUpByTypeRelatableAndIdRelatableDatatable() {
            this.datatable.loading = true
            this.fetching_follow_up_by_entity_relatable = true
            followUpService
                .getFollowUpByTypeRelatableAndIdRelatableDatatable(
                    this.datatable.buildUrlParams(),
                    this.typeRelatable,
                    this.idRelatable,
                    this.display_traceability
                )
                .then((success) => {
                    this.followUps = success.data.data.data
                    this.datatable.total = success.data.data.total_rows
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.datatable.loading = false
                    this.fetching_follow_up_by_entity_relatable = false
                })
        },
        openPutUpdateFollowUpDialog(note) {
            this.$refs.addFollowUpDialog.openPutUpdateFollowUpDialog(note)
            this.note = note
        },
        parseDate(date) {
            return JSON.parse(date)?.date
        },
    },
    watch: {
        'datatable.itemsPerPage': {
            handler() {
                this.fetchFollowUpByTypeRelatableAndIdRelatableDatatable()
            },
        },
        'datatable.currentPage': {
            handler() {
                this.fetchFollowUpByTypeRelatableAndIdRelatableDatatable()
            },
        },
        'datatable.search': {
            handler() {
                this.fetchFollowUpByTypeRelatableAndIdRelatableDatatable()
            },
        },
    },
    async mounted() {
        if (this.userHasAccess(this.asset_module.designation, this.role_vision)) {
            this.fetchFollowUpByTypeRelatableAndIdRelatableDatatable()
        }
    },
}
</script>

<style>
.elements-per-page-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-self: flex-end;
    align-items: flex-end;
}

.select-elements-per-page {
    width: 70px;
}
</style>
